import React, { useState, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';

const BraceletCalculator = () => {
  const location = useLocation();
  
  // 接收来自配色灵感库的配色方案
  const [colorPalette, setColorPalette] = useState(null);
  
  // 筋围计算器状态
  const [wristCircumference, setWristCircumference] = useState('');
  const [braceletType, setBraceletType] = useState('elastic');
  const [braceletLength, setBraceletLength] = useState('');

  // 珠子计算器状态
  const [braceletLength2, setBraceletLength2] = useState(16);
  const [beadDiameter, setBeadDiameter] = useState(10);
  const [beadCount, setBeadCount] = useState(16);

  // 多圈计算器状态
  const [circleCount, setCircleCount] = useState(1);
  const [adjustValue, setAdjustValue] = useState(0);
  
  // 接收配色方案数据
  useEffect(() => {
    if (location.state && location.state.colorPalette) {
      setColorPalette(location.state.colorPalette);
    }
  }, [location.state]);

  // 计算珠子数量
  const calculateBeadCount = React.useCallback(() => {
    const bl = parseFloat(braceletLength2);
    const bd = parseFloat(beadDiameter);
    if (bl && bd) {
      // 将珠子直径从mm转换为cm，然后计算数量
      // 正确公式：珠子数量 = 总长度(cm) * 10 / 珠子直径(mm)
      const count = Math.round((bl * 10) / bd);
      setBeadCount(count);
    }
  }, [braceletLength2, beadDiameter]);

  // 初始化计算
  React.useEffect(() => {
    calculateBeadCount();
  }, [calculateBeadCount]);

  // 使用useMemo计算总长度，避免无限重渲染
  const totalLength = useMemo(() => {
    // 使用筋围计算结果，如果没有则使用默认值16
    const baseLength = braceletLength ? parseFloat(braceletLength) : 16;
    return (baseLength * circleCount + adjustValue).toFixed(1);
  }, [braceletLength, circleCount, adjustValue]);

  // 当总长度变化时，自动更新珠子计算器
  useEffect(() => {
    setBraceletLength2(parseFloat(totalLength));
    calculateBeadCount();
  }, [totalLength, calculateBeadCount]);

  // 计算筋围
  const calculateBraceletLength = () => {
    if (!wristCircumference) return;
    const wc = parseFloat(wristCircumference);
    let length = wc;
    
    // 根据手链类型调整长度
    if (braceletType === 'elastic') {
      length = wc + 1;
    } else if (braceletType === 'non-elastic') {
      length = wc + 2;
    } else if (braceletType === 'chain') {
      length = wc + 3;
    }
    
    const finalLength = length.toFixed(1);
    setBraceletLength(finalLength);
    
    // 自动填充到珠子计算器
    setBraceletLength2(parseFloat(finalLength));
    
    // 更新多圈计算器的默认长度
    calculateBeadCount();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-100 to-blue-100 py-8">
      <div className="container mx-auto px-4">
        <h1 className="text-3xl font-bold text-center mb-8 text-purple-800">
          手链计算器
        </h1>
        <p className="text-gray-600 text-center mb-10">
          几个小工具，帮你做出完美手链
        </p>
        
        {/* 配色方案展示 */}
        {colorPalette && (
          <div className="bg-white rounded-xl shadow-lg p-6 mb-8 border-2 border-blue-500">
            <h2 className="text-xl font-semibold mb-4 text-center text-blue-700">
              💎 已应用配色方案
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <h3 className="text-lg font-medium text-blue-800 mb-2">{colorPalette.name}</h3>
                <div className="text-gray-600 mb-4">
                  适合水晶：{colorPalette.suitableCrystals.join('、')}
                </div>
              </div>
              <div className="flex flex-wrap gap-3 items-center">
                {colorPalette.colors.map((color, index) => (
                  <div
                    key={index}
                    className="flex items-center gap-2"
                  >
                    <div
                      className="w-10 h-10 rounded-full border-2 border-white shadow-md"
                      style={{ backgroundColor: color }}
                      title={color}
                    ></div>
                    <span className="text-sm text-gray-600">{color}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* 筋围计算器 */}
        <div className="bg-white rounded-xl shadow-lg p-6 border border-blue-100 mb-8">
          <h2 className="text-xl font-semibold mb-4 text-center text-blue-700">
            1. 腕围计算器
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="space-y-4">
              <div>
                <label className="block text-gray-600 mb-2 text-sm">输入腕围(cm)</label>
                <input
                  type="number"
                  value={wristCircumference}
                  onChange={(e) => setWristCircumference(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="请输入腕围"
                />
              </div>
              <div>
                <label className="block text-gray-600 mb-2 text-sm">特性</label>
                <select
                  value={braceletType}
                  onChange={(e) => setBraceletType(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="elastic">弹性</option>
                  <option value="non-elastic">非弹性</option>
                  <option value="chain">链条款</option>
                </select>
              </div>
              <button
                onClick={calculateBraceletLength}
                className="w-full bg-gradient-to-r from-blue-500 to-teal-500 text-white py-2 rounded-lg hover:from-blue-600 hover:to-teal-600 transition-colors duration-200"
              >
                计算手链长度
              </button>
              <div>
                <div className="text-gray-600 mb-2 text-sm">手链长度：</div>
                <div className="text-xl font-bold text-blue-800">{braceletLength || '---'} cm</div>
              </div>
            </div>

            {/* 咪数推荐器 */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-center text-pink-700">
                咪数推荐
              </h3>
              <div className="text-center">
                <div className="flex items-center justify-center gap-2 mb-4">
                  <span className="text-gray-600">腕围: {wristCircumference || '---'} cm</span>
                </div>
                <div className="flex justify-center gap-4">
                  <div className="text-center">
                    <div className="w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center mb-2 mx-auto">
                      <span className="text-blue-800 font-bold">小咪数</span>
                    </div>
                    <div className="text-sm text-gray-600">4-8mm</div>
                  </div>
                  <div className="text-center">
                    <div className="w-16 h-16 rounded-full bg-orange-100 flex items-center justify-center mb-2 mx-auto">
                      <span className="text-orange-800 font-bold">中咪数</span>
                    </div>
                    <div className="text-sm text-gray-600">8-12mm</div>
                  </div>
                  <div className="text-center">
                    <div className="w-16 h-16 rounded-full bg-pink-100 flex items-center justify-center mb-2 mx-auto">
                      <span className="text-pink-800 font-bold">大咪数</span>
                    </div>
                    <div className="text-sm text-gray-600">12-16mm</div>
                  </div>
                </div>
              </div>
              <div className="text-center">
                <div className="text-sm text-gray-600 mb-2">
                  推荐：{braceletLength && parseFloat(braceletLength) < 17 ? '小咪数' : parseFloat(braceletLength) < 19 ? '中咪数' : '大咪数'}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 多圈计算器 */}
        <div className="bg-white rounded-xl shadow-lg p-6 border border-purple-100 mb-8">
          <h2 className="text-xl font-semibold mb-4 text-center text-purple-700">
            2. 多圈计算器
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <div className="space-y-4">
                <div>
                  <label className="block text-gray-600 mb-2 text-sm">圈数</label>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setCircleCount(Math.max(1, circleCount - 1))}
                      className="px-4 py-2 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors duration-200"
                    >
                      - 单圈
                    </button>
                    <button
                      onClick={() => setCircleCount(2)}
                      className="px-4 py-2 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors duration-200"
                    >
                      双圈
                    </button>
                    <button
                      onClick={() => setCircleCount(3)}
                      className="px-4 py-2 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors duration-200"
                    >
                      三圈
                    </button>
                    <button
                      onClick={() => setCircleCount(Math.min(5, circleCount + 1))}
                      className="px-4 py-2 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors duration-200"
                    >
                      +
                    </button>
                  </div>
                </div>
                <div>
                  <label className="block text-gray-600 mb-2 text-sm">每圈长度调整 (cm)</label>
                  <input
                    type="range"
                    min="-2"
                    max="2"
                    step="0.1"
                    value={adjustValue}
                    onChange={(e) => setAdjustValue(parseFloat(e.target.value))}
                    className="w-full"
                  />
                  <div className="text-center mt-2 text-gray-600">
                    {adjustValue} cm
                  </div>
                </div>
              </div>
            </div>
            <div className="space-y-4">
              <div className="text-center">
                <div className="text-lg font-semibold text-purple-800 mb-4">
                  总长度：{totalLength} cm
                </div>
                <div className="text-sm text-gray-600">
                  提示：圈数越多，总长度越长
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 珠子计算器 */}
        <div className="bg-white rounded-xl shadow-lg p-6 border border-green-100">
          <h2 className="text-xl font-semibold mb-4 text-center text-green-700">
            3. 珠子计算器
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="space-y-4">
              <div>
                <label className="block text-gray-600 mb-2 text-sm">总长度(cm)</label>
                <input
                  type="number"
                  value={braceletLength2}
                  onChange={(e) => setBraceletLength2(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                />
              </div>
              <div>
                <label className="block text-gray-600 mb-2 text-sm">珠子直径(mm)</label>
                <input
                  type="number"
                  value={beadDiameter}
                  onChange={(e) => setBeadDiameter(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                />
              </div>
              <button
                onClick={calculateBeadCount}
                className="w-full bg-gradient-to-r from-green-500 to-emerald-500 text-white py-2 rounded-lg hover:from-green-600 hover:to-emerald-600 transition-colors duration-200"
              >
                计算珠子数量
              </button>
            </div>
            <div className="space-y-4">
              <div className="text-center">
                <div className="text-xl font-bold text-green-800 mt-8">{beadCount} 颗</div>
                <div className="text-xs text-gray-500 mt-2">提示：多放2-3颗备用</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BraceletCalculator;